import { transformContactData } from "@/shared/utils/helperFunctions";
import axiosInstanceNode from "@/api/axiosInstanceNode";
import axiosInstanceAI, { LONG_REQUEST_TIMEOUT_MS } from "@/api/axiosInstanceAI";

/** Not sent on `/contact`; applied only on the client (transformed payload). */
const CONTACT_LIST_FILTERS_CLIENT_ONLY = new Set([]);

/*Fetch contacts */
export const fetchContactsAPI = async (pageNum = 1, limitNum = 10, searchQuery = "", appliedFilters = {}) => {
  try {
    const params = new URLSearchParams({
      page: pageNum.toString(),
      limit: limitNum.toString(),
      
    });

    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (CONTACT_LIST_FILTERS_CLIENT_ONLY.has(key)) return;
      if (Array.isArray(value) && value.length > 0) {
        params.append(key, value.join(","));
      } else if (typeof value === "string" && value.trim()) {
        params.append(key, value.trim());
      }
    });
    

    if (searchQuery) {
      params.append("search", searchQuery);
    }

    const response = await axiosInstanceNode.get(`/contact/?${params.toString()}`);
    
    const contactsArray = response.data?.data || [];
    const totalCount = response.data?.total || 0;

    const transformedContacts = contactsArray.map(transformContactData);
    
    return {
      contacts: transformedContacts,
      total: totalCount,
      page: pageNum,
      limit: limitNum,
    };
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to fetch contacts. Please refresh the page."
    );
  }
};

/** Same filters as list; returns all matching ids (server-capped). */
export const fetchAllContactIdsAPI = async (searchQuery = "", appliedFilters = {}) => {
  const params = new URLSearchParams();
  Object.entries(appliedFilters).forEach(([key, value]) => {
    if (CONTACT_LIST_FILTERS_CLIENT_ONLY.has(key)) return;
    if (Array.isArray(value) && value.length > 0) {
      params.append(key, value.join(","));
    } else if (typeof value === "string" && value.trim()) {
      params.append(key, value.trim());
    }
  });
  if (searchQuery) params.append("search", searchQuery);
  try {
    const response = await axiosInstanceNode.get(`/contact/ids?${params.toString()}`);
    return response.data?.ids ?? [];
  } catch (error) {
    throw new Error(
      error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to fetch contact ids.",
    );
  }
};

export const previewCSVAPI = async (file) => {
  if (!file) throw new Error("Please select a CSV file.");
  const formData = new FormData();
  formData.append("file", file, file.name);
  try {
    const response = await axiosInstanceNode.post(
      "/contact/import/csv/preview",
      formData,
      {
        headers: {},
        timeout: 60000,
        skipGlobalErrorToast: true,
      },
    );
    return {
      headers: response.data?.headers ?? [],
      sampleRows: response.data?.sampleRows ?? [],
    };
  } catch (error) {
    const msg =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message;
    throw new Error(msg || "Failed to preview CSV.");
  }
};

/*Upload CSV file to import contacts */
// dryRun: only counts rows missing optional step fields; skipMissing: field keys whose empty rows are skipped.
export const uploadContactsCSV = async (file, columnMapping = null, { dryRun = false, skipMissing = [] } = {}) => {
  if (!file) {
    throw new Error("Please select a CSV file.");
  }

  // Validate file type
  const validTypes = [
    "text/csv",
    "application/vnd.ms-excel",
    "application/csv",
    "text/x-csv",
    "application/x-csv",
    "text/comma-separated-values",
    "text/x-comma-separated-values",
  ];

  const isValidType =
    file.name.toLowerCase().endsWith(".csv") || validTypes.includes(file.type);

  if (!isValidType) {
    throw new Error("Please select a valid CSV file.");
  }

  // Check file size (max 10MB)
  const maxSize = 10 * 1024 * 1024;
  if (file.size > maxSize) {
    throw new Error("File size exceeds 10MB. Please select a smaller file.");
  }

  const formData = new FormData();
  formData.append("file", file, file.name);
  if (
    columnMapping &&
    typeof columnMapping === "object" &&
    Object.keys(columnMapping).length > 0
  ) {
    formData.append("columnMapping", JSON.stringify(columnMapping));
  }
  if (dryRun) formData.append("dryRun", "true");
  if (skipMissing.length) formData.append("skipMissing", JSON.stringify(skipMissing));

  try {
    const response = await axiosInstanceNode.post("/contact/import/csv", formData, {
      headers: {},
      timeout: 120000,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to upload contacts. Please refresh the page.",
    );
  }
};

/*Delete contact */
export const deleteContactAPI = async (contactId) => {
  try {
    const response = await axiosInstanceNode.delete(`/contact/${contactId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to delete contact. Please refresh the page.");
  }
};

/*Bulk delete contacts */
export const bulkDeleteContactsAPI = async ({contactIds}) => {
  try {
    const response = await axiosInstanceNode.post(`/contact/bulk-delete`, {
      contactIds: contactIds,
    });
    return response.data;
  } catch (error) {
    const status = error.response?.status;
    const data = error.response?.data;
    const msg =
      (typeof data === "string" && data) ||
      data?.message ||
      data?.error ||
      (status === 413
        ? "Request too large. Try deleting fewer contacts at once, or contact support."
        : null);
    throw new Error(
      msg || "Failed to delete contacts. Please refresh the page.",
    );
  }
};

// add contact manuallly (skipGlobalErrorToast: caller maps 400s to form fields)
export const addContactManuallyAPI = async (contactData) => {
  try {
    const response = await axiosInstanceNode.post("/contact/create", contactData, {
      skipGlobalErrorToast: true,
    });
    return response.data;
  } catch (error) {
    const serverMsg =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message;
    throw new Error(
      serverMsg || "Failed to add contact. Please refresh the page.",
    );
  }
};

// generate match score
export const generateMatchScoreAPI = async ({userId, contactIds = []}) => {
  try {
    const response = await axiosInstanceAI.post(
      "/lead-scoring/bulk-match-score",
      { user_id: userId, contacts: contactIds },
      // N contacts x M campaigns of LLM scoring; runs well past the default.
      { timeout: LONG_REQUEST_TIMEOUT_MS },
    );
    return response.data;
  } catch (error) {
    if (error?.response?.status === 503) {
      throw new Error("Something went wrong from our side. Please try again after sometime.");
    }
    throw new Error(error.response?.data?.message || error.response?.data?.detail || "Failed to generate match score. Please refresh the page.");
  }
};


// generate match score for a single contact
export const generateMatchScoreForSingleContactAPI = async ({userId, contactId}) => {
  try {
    const response = await axiosInstanceAI.post("/lead-scoring/match-score", {
      user_id: userId,
      contact_id: contactId,
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.response?.data?.detail || "Failed to generate match score. Please refresh the page.");
  }
};

// update contact
export const updateContactAPI = async ({ contactId, contactData }) => {
  try {
    const response = await axiosInstanceNode.put(`/contact/${contactId}`, contactData);
    return response.data;
  } catch (error) {
    const serverMsg =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message;
    throw new Error(
      serverMsg || "Failed to update contact. Please refresh the page.",
    );
  }
};

export const updateContactConsentAPI = async ({ contactId, status, source, note }) => {
  try {
    const response = await axiosInstanceNode.patch(`/contact/${contactId}/email-consent`, {
      status,
      source,
      note,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to update email consent.",
    );
  }
};

export const bulkUpdateContactConsentAPI = async ({ contactIds, status, source, note }) => {
  try {
    const response = await axiosInstanceNode.patch(`/contact/email-consent/bulk`, {
      contactIds,
      status,
      source,
      note,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to update email consent.",
    );
  }
};

export const fetchContactDetailAPI = async (contactId) => {
  try {
    const response = await axiosInstanceNode.get(`/contact/${contactId}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to load contact details.",
    );
  }
};

export const fetchContactGroupsAPI = async (groupBy, pageNum = 1, limitNum = 10, searchQuery = "", appliedFilters = {}) => {
  try {
    const params = new URLSearchParams({
      groupBy,
      page: pageNum.toString(),
      limit: limitNum.toString(),
    });

    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (CONTACT_LIST_FILTERS_CLIENT_ONLY.has(key)) return;
      if (Array.isArray(value) && value.length > 0) {
        params.append(key, value.join(","));
      } else if (typeof value === "string" && value.trim()) {
        params.append(key, value.trim());
      }
    });

    if (searchQuery) {
      params.append("search", searchQuery);
    }

    const response = await axiosInstanceNode.get(`/contact/groups?${params.toString()}`);
    return {
      groups: response.data?.data || [],
      total: response.data?.total || 0,
      page: pageNum,
      limit: limitNum,
    };
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to fetch contact groups. Please refresh the page."
    );
  }
};

export const fetchGroupContactsAPI = async (groupBy, groupId, limitNum = 20, cursor = null, searchQuery = "", appliedFilters = {}, sortDescriptor = {}) => {
  try {
    const params = new URLSearchParams({
      groupBy,
      limit: limitNum.toString(),
    });

    if (cursor) {
      params.append("cursor", cursor);
    }

    if (sortDescriptor?.column) {
      params.append("sortColumn", sortDescriptor.column);
      params.append("sortDirection", sortDescriptor.direction === "descending" ? "DESC" : "ASC");
    }

    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (CONTACT_LIST_FILTERS_CLIENT_ONLY.has(key)) return;
      if (Array.isArray(value) && value.length > 0) {
        params.append(key, value.join(","));
      } else if (typeof value === "string" && value.trim()) {
        params.append(key, value.trim());
      }
    });

    if (searchQuery) {
      params.append("search", searchQuery);
    }

    const encodedGroupId = encodeURIComponent(groupId);
    const response = await axiosInstanceNode.get(`/contact/groups/${encodedGroupId}?${params.toString()}`);
    
    const contactsArray = response.data?.contacts || [];
    const transformedContacts = contactsArray.map(transformContactData);

    return {
      contacts: transformedContacts,
      nextCursor: response.data?.nextCursor || null,
      hasMore: response.data?.hasMore || false,
    };
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to fetch group contacts. Please refresh the page."
    );
  }
};
